import React, { useState } from 'react';
import type { ConflictingMigration } from '../../types';
import ConflictingMigrationsList from './ConflictingMigrationsList';
import Spinner from '../migratorDashboard/Spinner';
import { ui } from '../../config/uiSizes';

interface RecurringMigrationConflictBannerProps {
    /** Whether the conflict check is active (recurring on + a target org resolved, for org-to-org or division-to-division). */
    active: boolean;
    loading: boolean;
    hasConflict: boolean;
    conflicts: ConflictingMigration[];
    /** Friendly display name of the selected target organization. */
    targetOrgName: string;
    onViewDetails: (migrationId: string) => void;
    resolveOrgName: (storedName: string) => string;
}

const RecurringMigrationConflictBanner: React.FC<RecurringMigrationConflictBannerProps> = ({
    active,
    loading,
    hasConflict,
    conflicts,
    targetOrgName,
    onViewDetails,
    resolveOrgName,
}) => {
    const [expanded, setExpanded] = useState(false);

    if (!active) return null;

    if (loading) {
        return (
            <div
                className={`bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg ${ui.page.sectionPadding} mt-4`}
                role="status"
                aria-live="polite"
            >
                <div className={`flex items-center ${ui.gap.sm}`}>
                    <Spinner />
                    <span className={`${ui.text.body} text-gray-600 dark:text-gray-300`}>
                        Checking for existing recurring migrations for {targetOrgName}…
                    </span>
                </div>
            </div>
        );
    }

    if (!hasConflict) {
        return (
            <div
                className={`bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg ${ui.page.sectionPadding} mt-4`}
                role="status"
                aria-live="polite"
            >
                <div className={`flex items-center ${ui.gap.sm}`}>
                    <svg
                        className={`${ui.svg.sm} text-gray-400 dark:text-gray-500 shrink-0`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    <span className={`${ui.text.body} text-gray-600 dark:text-gray-300`}>
                        No existing recurring migrations for {targetOrgName} — none listed.
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`bg-yellow-50 dark:bg-yellow-700/30 border border-yellow-300 dark:border-yellow-600 rounded-lg ${ui.page.sectionPadding} mt-4`}
            role="alert"
            aria-live="polite"
        >
            <div className={`flex items-start ${ui.gap.sm}`}>
                <svg
                    className={`${ui.svg.sm} text-yellow-600 dark:text-yellow-300 shrink-0 mt-0.5`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                </svg>
                <div className="flex-1">
                    <p className={`${ui.text.label} font-semibold text-yellow-800 dark:text-yellow-200`}>
                        A recurring migration already exists for {targetOrgName}.
                    </p>
                    <button
                        type="button"
                        onClick={() => setExpanded((prev) => !prev)}
                        className={`${ui.text.xs} text-yellow-700 dark:text-yellow-300 underline hover:no-underline mt-1 focus:outline-none focus:ring-2 focus:ring-yellow-500 rounded`}
                        aria-expanded={expanded}
                    >
                        {expanded ? 'Hide existing migrations' : 'Review existing migrations'}
                    </button>
                    {expanded && <ConflictingMigrationsList conflicts={conflicts} onViewDetails={onViewDetails} resolveOrgName={resolveOrgName} />}
                </div>
            </div>
        </div>
    );
};

export default RecurringMigrationConflictBanner;
